import React, { useEffect, useRef, useState } from 'react';
import { Box, Typography, IconButton, CircularProgress } from '@mui/material';
import { Close } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useOverlayBackButton } from '../../hooks/useOverlayBackButton';
import type { SyncedLyricLine } from '../../services/sonosApi';
import { findActiveLyricIndex } from './lyricsLrc';
import { SONOS_BLUE, sonosIconTapSx } from './sonosUiStyles';

export interface LyricsOverlayProps {
  open: boolean;
  onClose: () => void;
  loading: boolean;
  error: 'not_found' | 'network' | null;
  instrumental: boolean;
  plainLyrics: string | null;
  syncedLines: SyncedLyricLine[];
  livePositionSec: number | null;
}

const LyricsOverlay: React.FC<LyricsOverlayProps> = ({
  open,
  onClose,
  loading,
  error,
  instrumental,
  plainLyrics,
  syncedLines,
  livePositionSec,
}) => {
  const { t } = useTranslation();
  const requestClose = useOverlayBackButton(open, onClose, 'sonos-lyrics-overlay');
  const activeLineRef = useRef<HTMLDivElement | null>(null);
  const userScrolledUntilRef = useRef(0);
  const [activeIdx, setActiveIdx] = useState(-1);

  useEffect(() => {
    if (!open) {
      setActiveIdx(-1);
      return;
    }
    setActiveIdx(findActiveLyricIndex(syncedLines, livePositionSec));
  }, [open, syncedLines, livePositionSec]);

  useEffect(() => {
    if (!open || activeIdx < 0) return;
    if (Date.now() < userScrolledUntilRef.current) return;
    activeLineRef.current?.scrollIntoView({ block: 'center', behavior: 'smooth' });
  }, [open, activeIdx]);

  if (!open) return null;

  const hasSynced = syncedLines.length > 0;
  const emptyMessage = (() => {
    if (loading) return null;
    if (error === 'network') return t('musicSystem.player.lyrics.errorNetwork');
    if (error === 'not_found') return t('musicSystem.player.lyrics.notFound');
    if (instrumental) return t('musicSystem.player.lyrics.instrumental');
    if (!hasSynced && !plainLyrics) return t('musicSystem.player.lyrics.notFound');
    return null;
  })();

  return (
    <Box
      data-audio-no-swipe=""
      sx={{
        position: 'absolute',
        left: 0,
        right: 0,
        top: 0,
        bottom: 0,
        zIndex: 5,
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: 'rgba(18, 18, 22, 0.52)',
        backdropFilter: 'blur(6px)',
        WebkitBackdropFilter: 'blur(6px)',
        borderBottom: '1px solid rgba(255,255,255,0.06)',
        px: 2.5,
        pt: 1,
        pb: 2,
      }}
    >
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        mb: 1,
        flexShrink: 0,
      }}>
        <Typography sx={{
          fontSize: 11,
          fontWeight: 800,
          letterSpacing: 0.8,
          textTransform: 'uppercase',
          color: '#888',
        }}>
          {t('musicSystem.player.lyrics.header')}
        </Typography>
        <IconButton
          size="small"
          onClick={requestClose}
          disableRipple
          aria-label={t('musicSystem.player.lyrics.close') as string}
          sx={{ color: '#999', p: 0.25, ...sonosIconTapSx }}
        >
          <Close sx={{ fontSize: 16 }} />
        </IconButton>
      </Box>

      <Box
        onScroll={() => {
          userScrolledUntilRef.current = Date.now() + 3000;
        }}
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          WebkitOverflowScrolling: 'touch',
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
          '&::-webkit-scrollbar': { display: 'none' },
          display: 'flex',
          flexDirection: 'column',
          alignItems: emptyMessage || loading ? 'center' : 'flex-start',
          justifyContent: emptyMessage || loading ? 'center' : 'flex-start',
          gap: hasSynced ? '12px' : 0,
          py: 1,
        }}
      >
        {loading && (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5 }}>
            <CircularProgress size={28} sx={{ color: SONOS_BLUE }} />
            <Typography sx={{ color: '#888', fontSize: 16 }}>
              {t('musicSystem.player.lyrics.loading')}
            </Typography>
          </Box>
        )}

        {!loading && emptyMessage && (
          <Typography sx={{
            color: error === 'network' ? '#c62828' : '#888',
            fontSize: 16,
            textAlign: 'center',
            px: 2,
          }}>
            {emptyMessage}
          </Typography>
        )}

        {!loading && !emptyMessage && hasSynced && syncedLines.map((line, i) => {
          const active = i === activeIdx;
          return (
            <Typography
              key={`${line.start_sec}-${i}`}
              ref={active ? activeLineRef : undefined}
              sx={{
                fontSize: active ? 22 : 19,
                fontWeight: active ? 700 : 400,
                lineHeight: active ? 1.45 : 1.5,
                color: active ? SONOS_BLUE : '#c8c8c8',
                opacity: active ? 1 : 0.72,
                transition: 'color 0.2s ease, opacity 0.2s ease, font-size 0.15s ease',
                width: '100%',
              }}
            >
              {line.text || '\u00a0'}
            </Typography>
          );
        })}

        {!loading && !emptyMessage && !hasSynced && plainLyrics && (
          <Typography sx={{
            color: '#c8c8c8',
            opacity: 0.92,
            fontSize: 19,
            fontWeight: 400,
            lineHeight: 1.5,
            whiteSpace: 'pre-wrap',
            width: '100%',
          }}>
            {plainLyrics}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default LyricsOverlay;
